import { useCallback, useMemo, useState } from 'react';
import { api } from '../../api/client.js';
import { STORE_CATALOG_LUXE_EDITOR_EXAMPLE } from '../../theme/storeCatalogLuxe.js';

/** @param {string} s */
function parseBody(s) {
  try {
    const o = JSON.parse(s.trim() === '' ? '{}' : s);
    if (!o || typeof o !== 'object' || Array.isArray(o)) {
      return { ok: false, error: 'O JSON tem de ser um objecto.', body: null };
    }
    return { ok: true, error: '', body: /** @type {Record<string, unknown>} */ (o) };
  } catch {
    return { ok: false, error: 'JSON inválido.', body: null };
  }
}

/**
 * @param {{ tenantId: number | null; json: string; onJsonChange: (s: string) => void; disabled?: boolean }} props
 */
export function StoreCatalogLuxeForm({ tenantId, json, onJsonChange, disabled = false }) {
  const parsed = useMemo(() => parseBody(json), [json]);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadErr, setUploadErr] = useState('');

  const body = parsed.ok && parsed.body ? parsed.body : STORE_CATALOG_LUXE_EDITOR_EXAMPLE;

  const commit = useCallback(
    (next) => {
      onJsonChange(JSON.stringify(next, null, 2));
    },
    [onJsonChange],
  );

  const hero = body.hero && typeof body.hero === 'object' ? body.hero : {};
  const story = body.story && typeof body.story === 'object' ? body.story : {};
  const catalog = body.catalog && typeof body.catalog === 'object' ? body.catalog : {};
  const marquee = body.marquee && typeof body.marquee === 'object' ? body.marquee : { items: [] };
  const howItWorks = body.howItWorks && typeof body.howItWorks === 'object' ? body.howItWorks : {};
  const customBlock = body.customBlock && typeof body.customBlock === 'object' ? body.customBlock : {};

  function patchHero(key, value) {
    commit({ ...body, hero: { ...hero, [key]: value } });
  }

  function patchStory(key, value) {
    commit({ ...body, story: { ...story, [key]: value } });
  }

  function patchCatalog(key, value) {
    commit({ ...body, catalog: { ...catalog, [key]: value } });
  }

  async function uploadHeroImage(file) {
    if (!tenantId || !file) return;
    setUploadErr('');
    setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append('imagem', file);
      const r = await api(`/tenants/${tenantId}/vitrine-hero-upload`, { method: 'POST', body: fd });
      const url = r?.url;
      if (typeof url === 'string' && url.trim()) {
        patchHero('image_url', url.trim());
      } else {
        setUploadErr('Resposta sem URL da imagem.');
      }
    } catch (e) {
      setUploadErr(e?.message || 'Falha no envio.');
    } finally {
      setUploadBusy(false);
    }
  }

  const marqueeText = Array.isArray(marquee.items) ? marquee.items.join('\n') : '';

  return (
    <div className="space-y-4 rounded-2xl border border-violet-500/25 bg-violet-500/[0.04] p-4 dark:border-violet-400/20 dark:bg-violet-950/15">
      <p className="text-xs font-semibold text-violet-950 dark:text-violet-100">Textos do catálogo luxo</p>

      <label className="block text-xs font-semibold">
        Destaque — etiqueta
        <input
          className="mt-1 w-full rounded-lg border border-ink/10 px-2 py-1.5 text-sm dark:border-white/10 dark:bg-[#252f2a]"
          value={String(hero.badge || '')}
          disabled={disabled}
          onChange={(e) => patchHero('badge', e.target.value)}
        />
      </label>
      <label className="block text-xs font-semibold">
        Título (linha 1)
        <input
          className="mt-1 w-full rounded-lg border border-ink/10 px-2 py-1.5 text-sm dark:border-white/10 dark:bg-[#252f2a]"
          value={String(hero.titleBefore || '')}
          disabled={disabled}
          onChange={(e) => patchHero('titleBefore', e.target.value)}
        />
      </label>
      <label className="block text-xs font-semibold">
        Título (destaque cursivo)
        <input
          className="mt-1 w-full rounded-lg border border-ink/10 px-2 py-1.5 text-sm dark:border-white/10 dark:bg-[#252f2a]"
          value={String(hero.titleAccent || '')}
          disabled={disabled}
          onChange={(e) => patchHero('titleAccent', e.target.value)}
        />
      </label>
      <label className="block text-xs font-semibold">
        Subtítulo do hero
        <textarea
          rows={3}
          className="mt-1 w-full rounded-lg border border-ink/10 px-2 py-1.5 text-sm dark:border-white/10 dark:bg-[#252f2a]"
          value={String(hero.subtitle || '')}
          disabled={disabled}
          onChange={(e) => patchHero('subtitle', e.target.value)}
        />
      </label>
      <label className="block text-xs font-semibold">
        Botão «Ver catálogo»
        <input
          className="mt-1 w-full rounded-lg border border-ink/10 px-2 py-1.5 text-sm dark:border-white/10 dark:bg-[#252f2a]"
          value={String(hero.ctaCatalog || '')}
          disabled={disabled}
          onChange={(e) => patchHero('ctaCatalog', e.target.value)}
        />
      </label>
      <div>
        <span className="text-xs font-semibold">Imagem do hero (opcional)</span>
        <input
          type="file"
          accept="image/*"
          disabled={disabled || uploadBusy || !tenantId}
          className="mt-1 block w-full text-xs"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) uploadHeroImage(f);
            e.target.value = '';
          }}
        />
        {hero.image_url ? (
          <p className="mt-1 break-all font-mono text-[10px] text-ink/60 dark:text-mist/60">{String(hero.image_url)}</p>
        ) : null}
        {uploadErr ? <p className="mt-1 text-xs text-accent2">{uploadErr}</p> : null}
      </div>

      <label className="block text-xs font-semibold">
        Faixa animada — uma linha por frase
        <textarea
          rows={4}
          className="mt-1 w-full rounded-lg border border-ink/10 px-2 py-1.5 text-sm dark:border-white/10 dark:bg-[#252f2a]"
          value={marqueeText}
          disabled={disabled}
          onChange={(e) => {
            const items = e.target.value
              .split('\n')
              .map((x) => x.trim())
              .filter(Boolean);
            commit({ ...body, marquee: { items } });
          }}
        />
      </label>

      <label className="block text-xs font-semibold">
        História — título
        <input
          className="mt-1 w-full rounded-lg border border-ink/10 px-2 py-1.5 text-sm dark:border-white/10 dark:bg-[#252f2a]"
          value={String(story.title || '')}
          disabled={disabled}
          onChange={(e) => patchStory('title', e.target.value)}
        />
      </label>
      <label className="block text-xs font-semibold">
        História — destaque cursivo
        <input
          className="mt-1 w-full rounded-lg border border-ink/10 px-2 py-1.5 text-sm dark:border-white/10 dark:bg-[#252f2a]"
          value={String(story.titleAccent || '')}
          disabled={disabled}
          onChange={(e) => patchStory('titleAccent', e.target.value)}
        />
      </label>

      <label className="block text-xs font-semibold">
        Catálogo — título (antes do destaque)
        <input
          className="mt-1 w-full rounded-lg border border-ink/10 px-2 py-1.5 text-sm dark:border-white/10 dark:bg-[#252f2a]"
          value={String(catalog.titleBefore || '')}
          disabled={disabled}
          onChange={(e) => patchCatalog('titleBefore', e.target.value)}
        />
      </label>
      <label className="block text-xs font-semibold">
        Catálogo — destaque cursivo
        <input
          className="mt-1 w-full rounded-lg border border-ink/10 px-2 py-1.5 text-sm dark:border-white/10 dark:bg-[#252f2a]"
          value={String(catalog.titleAccent || '')}
          disabled={disabled}
          onChange={(e) => patchCatalog('titleAccent', e.target.value)}
        />
      </label>

      <label className="flex cursor-pointer items-center gap-2 text-xs font-semibold">
        <input
          type="checkbox"
          checked={howItWorks.enabled !== false}
          disabled={disabled}
          onChange={(e) => commit({ ...body, howItWorks: { ...howItWorks, enabled: e.target.checked } })}
        />
        Mostrar secção «Como funciona»
      </label>

      <label className="flex cursor-pointer items-center gap-2 text-xs font-semibold">
        <input
          type="checkbox"
          checked={customBlock.enabled !== false}
          disabled={disabled}
          onChange={(e) => commit({ ...body, customBlock: { ...customBlock, enabled: e.target.checked } })}
        />
        Mostrar bloco «Sob medida» / WhatsApp
      </label>
    </div>
  );
}
